<script setup lang="ts">
import type { ConditionEntryFragment, GlossaryEntryFragment, NewsEntryFragment, PageArchiveEntryFragment, PageEntryFragment, PageGlossaryArchiveEntryFragment, TreatmentEntryFragment } from '#graphql-operations'

type BreadcrumbEntry
  = | ConditionEntryFragment
    | GlossaryEntryFragment
    | NewsEntryFragment
    | PageArchiveEntryFragment
    | PageEntryFragment
    | PageGlossaryArchiveEntryFragment
    | TreatmentEntryFragment

interface BreadcrumbsProps {
  data: BreadcrumbEntry
}

const props = defineProps<BreadcrumbsProps>()
const { t } = useI18n()
const { getArchiveSection } = useArchivePages()

const isTreatment = computed(() => props.data.__typename === 'treatmentDefault_Entry')
const ancestors = computed(() => 'ancestors' in props.data ? props.data.ancestors : [])

const section = computed(() => 'sectionHandle' in props.data ? getArchiveSection(props.data.sectionHandle) : undefined)

const currentItem = computed(() => {
  // if (isTreatment.value && 'heading' in props.data && props.data.heading)
  //   return { label: props.data.title ?? '', html: props.data.tit, current: true }

  return { label: props.data.title ?? '', current: true }
})

const overrides = computed(() => {
  if (isTreatment.value) {
    return [
      { label: t('home') },
      false as const,
      ...ancestors.value.map(ancestor => ({ to: ancestor.url, label: ancestor.title ?? '' })),
      currentItem.value,
    ]
  }

  if (section.value)
    return [{ label: t('home') }, section.value, currentItem.value]

  return [{ label: t('home') }, currentItem.value]
})

const breadcrumbs = useBreadcrumbItems({ overrides })
</script>

<template>
  <nav aria-label="breadcrumb" class="breadcrumbs typo-sans-1 leading-none mt-2.5 uppercase lg:mt-7">
    <ol class="flex gap-1" :class="{ 'justify-center': data.__typename === 'newsDefault_Entry' }">
      <li
        v-for="(item, index) in breadcrumbs"
        :key="item.to || item.label"
        :aria-current="item.current ? 'page' : undefined"
        :class="{
          'inline-flex': !item.current,
          'grid': item.current,
        }"
      >
        <Anchor v-if="!item.current" :to="getRelativeUrl(item.to)" :title="item.label" />
        <template v-else>
          <Text v-if="item.html" :html="item.html" class="truncate" />
          <Text v-else as="p" :text="item.label" class="truncate" />
        </template>
        <i v-if="index < breadcrumbs.length - 1" class="i-icon-chevron-right ml-1 vertical-middle flex-none icon" />
      </li>
    </ol>
  </nav>
</template>

<style scoped>
li :deep(br) {
  display: none;
}

li :deep(i) {
  font-style: normal;
}
</style>
